import React, { useState, createContext, useContext, useEffect } from 'react';import {
  LayoutDashboard,
  Calendar,
  MessageSquare,
  Star,
  Settings,
  Bell,
  LogOut,
  User,
  Briefcase,
  Clock,
  Zap,
  Image,
  HelpCircle,
  Crown,
  Menu,
  X
} from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './TalentDashboard.css';
import { API_BASE_URL } from '../../utils/api';

interface TalentLayoutProps {
  children: React.ReactNode;
}


// Unread count context (for sidebar dot)
interface UnreadContextType {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
}



const UnreadContext = createContext<UnreadContextType | undefined>(undefined);

export const useUnread = () => {
  const context = useContext(UnreadContext);
  if (!context) throw new Error('useUnread must be used within UnreadProvider');
  return context;
};

export const UnreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('skillseek_talent_token');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/messages/talent/conversations?limit=50&page=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          const total = (data.conversations || []).reduce(
            (sum: number, conv: any) => sum + (conv.unread_count || 0), 0
          );
          setUnreadCount(total);
        }
      } catch (e) {
        console.error('Unread fetch failed:', e);
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <UnreadContext.Provider value={{ unreadCount, setUnreadCount }}>
      {children}
    </UnreadContext.Provider>
  );
};


const TalentLayout: React.FC<TalentLayoutProps> = ({ children }) => {
  return (
    <UnreadProvider>
      <TalentLayoutContent children={children} />
    </UnreadProvider>
  );
};

// Inner component jo hook use karega
const TalentLayoutContent: React.FC<TalentLayoutProps> = ({ children }) => {
  const { unreadCount } = useUnread();   
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 991);
  const { talent, logout } = useTalentAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const token = localStorage.getItem('skillseek_talent_token');
      if (!token) return;
      
      const res = await fetch(`${API_BASE_URL}/notifications/talent?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadNotificationsCount(data.stats?.unread || 0);
      }
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      if (!token) return;
      
      const res = await fetch(`${API_BASE_URL}/notifications/talent/read-all`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadNotificationsCount(0);
      }
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  };

  const markNotificationAsRead = async (id: number) => {
    try {
      const token = localStorage.getItem('skillseek_talent_token');
      if (!token) return;
      
      const res = await fetch(`${API_BASE_URL}/notifications/talent/${id}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
      }
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.notification-container')) {
        setShowNotificationsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 991) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    // Initialize
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 991) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path;

  // Check if we're on the dashboard page
  const isDashboardPage = location.pathname === '/talent/dashboard';

  return (
    <div className="talent-dashboard">
      {/* Mobile Top Navbar */}
      <div className="mobile-top-navbar">
        <button className="mobile-menu-toggle" onClick={() => setIsSidebarOpen(true)}>
          <Menu size={24} />
        </button>
        <div className="mobile-brand" onClick={() => navigate('/')}>
          <Zap size={24} className="text-primary" />
          <span>SkillSeek</span>
        </div>
        <div className="mobile-header-actions">
          <div className="notification-container">
            <button 
              className="icon-button notification-btn" 
              onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
            >
              <Bell size={20} />
              {unreadNotificationsCount > 0 && (
                <span className="badge">{unreadNotificationsCount}</span>
              )}
            </button>
            
            {showNotificationsDropdown && (
              <div className="notification-dropdown">
                <div className="notification-dropdown-header">
                  <h3>Notifications</h3>
                  {unreadNotificationsCount > 0 && (
                    <button onClick={markAllNotificationsAsRead} className="mark-all-read-btn">
                      Mark all as read
                    </button>
                  )}
                </div>
                
                <div className="notification-dropdown-body">
                  {notificationsLoading && notifications.length === 0 ? (
                    <div className="notification-loading">Loading...</div>
                  ) : notifications.length === 0 ? (
                    <div className="no-notifications">No notifications yet</div>
                  ) : (
                    notifications.map(n => (
                      <div 
                        key={n.id} 
                        className={`notification-item ${!n.is_read ? 'unread' : ''}`}
                        onClick={() => markNotificationAsRead(n.id)}
                      >
                        <div className="notification-item-content">
                          <h4>{n.title}</h4>
                          <p>{n.message}</p>
                          <span className="notification-time">
                            {new Date(n.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        {!n.is_read && <span className="unread-dot"></span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <button className="profile-avatar-btn" onClick={() => navigate('/talent/profile')}>
            <div className="profile-avatar">
              <img
                src={talent?.profilePicture || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSI4IiB5PSI4Ij4KPHBhdGggZD0iTTIwIDIxVjE5QTQgNCAwIDAgMCAxNiAxNUg4QTQgNCAwIDAgMCAxIDE5VjIxIiBzdHJva2U9IiM5Q0E0QUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxjaXJjbGUgY3g9IjEyIiBjeT0iOSIgcj0iNCIgc3Ryb2tlPSIjOUNBNEFGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4KPC9zdmc+'}
                alt="Profile"
                className="profile-image-small"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSI4IiB5PSI4Ij4KPHBhdGggZD0iTTIwIDIxVjE5QTQgNCAwIDAgMCAxNiAxNUg4QTQgNCAwIDAgMCAxIDE5VjIxIiBzdHJva2U9IiM5Q0E0QUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxjaXJjbGUgY3g9IjEyIiBjeT0iOSIgcj0iNCIgc3Ryb2tlPSIjOUNBNEFGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4KPC9zdmc+';
                }}
              />
            </div>
          </button>
        </div>
      </div>

      <div className='open-toggle' onClick={() => setIsSidebarOpen(true)}>
        <Menu size={24} />
      </div>
      {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>

          <div className="sidebar-header d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <Zap size={32} className="text-primary" />
              <h1>SkillSeek</h1>
            </div>
            <div className='close-toggle' onClick={() => setIsSidebarOpen(false)}>
              <X size={24} />
            </div>
          </div>

          <nav className="sidebar-nav">
            {/* Overview Section */}
            <div className="nav-section">
              <div className="nav-section-title">OVERVIEW</div>
              <button
                onClick={() => navigate('/talent/dashboard')}
                className={`nav-item ${isActive('/talent/dashboard') ? 'active' : ''}`}
              >
                <LayoutDashboard size={20} />
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => navigate('/talent/profile')}
                className={`nav-item ${isActive('/talent/profile') ? 'active' : ''}`}
              >
                <User size={20} />
                <span>Personal information</span>
              </button>
              {/* <button 
              onClick={() => navigate('/talent/portfolio')} 
              className={`nav-item ${isActive('/talent/portfolio') ? 'active' : ''}`}
            >
              <Image size={20} />
              <span>Portfolio</span>
            </button> */}
              <button
                onClick={() => navigate('/talent/services')}
                className={`nav-item ${isActive('/talent/services') ? 'active' : ''}`}
              >
                <Briefcase size={20} />
                <span>My Services</span>
              </button>
              <button
                onClick={() => navigate('/talent/membership')}
                className={`nav-item ${isActive('/talent/membership') ? 'active' : ''}`}
              >
                <Crown size={20} />
                <span>Subscription</span>
              </button>
              {/* <button 
              onClick={() => navigate('/talent/schedule')} 
              className={`nav-item ${isActive('/talent/schedule') ? 'active' : ''}`}
            >
              <Clock size={20} />
              <span>Schedule</span>
            </button> */}
<button
  onClick={() => navigate('/talent/inquiries')}
  className={`nav-item ${isActive('/talent/inquiries') ? 'active' : ''}`}
>
  <MessageSquare size={20} />
  <span>Inquiries</span>
  {unreadCount > 0 && (
    // BAAD MEIN - position absolute use karo
<span style={{
  width: '8px',
  height: '8px',
  backgroundColor: '#185FA5',
  borderRadius: '50%',
  display: 'inline-block',
  flexShrink: 0,
  position: 'absolute',
  right: '16px',
}}></span>
  )}
</button>
              <button
                onClick={() => navigate('/talent/reviews')}
                className={`nav-item ${isActive('/talent/reviews') ? 'active' : ''}`}
              >
                <Star size={20} />
                <span>Reviews</span>
              </button>
              <button
                onClick={() => navigate('/')}
                className={`nav-item`}
              >
                <Settings size={20} />
                <span>View Website</span>
              </button>

            </div>




          </nav>

          <button onClick={handleLogout} className="logout-button">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </aside>

        {/* Sidebar Overlay for Mobile */}
        {isSidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
        )}

        {/* Main Content */}
        <main className="dashboard-main">
          {/* Header - Only show on dashboard page */}
          {isDashboardPage && (
            <header className="dashboard-header">
              <div className="header-welcome">
                <h2>Welcome back, {talent?.firstName}!</h2>
                <p>Manage your business and grow your success.</p>
              </div>

              <div className="header-actions">
                <div className="notification-container">
                  <button 
                    className="icon-button notification-btn" 
                    onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                  >
                    <Bell size={20} />
                    {unreadNotificationsCount > 0 && (
                      <span className="badge">{unreadNotificationsCount}</span>
                    )}
                  </button>
                  
                  {showNotificationsDropdown && (
                    <div className="notification-dropdown">
                      <div className="notification-dropdown-header">
                        <h3>Notifications</h3>
                        {unreadNotificationsCount > 0 && (
                          <button onClick={markAllNotificationsAsRead} className="mark-all-read-btn">
                            Mark all as read
                          </button>
                        )}
                      </div>
                      
                      <div className="notification-dropdown-body">
                        {notificationsLoading && notifications.length === 0 ? (
                          <div className="notification-loading">Loading...</div>
                        ) : notifications.length === 0 ? (
                          <div className="no-notifications">No notifications yet</div>
                        ) : (
                          notifications.map(n => (
                            <div 
                              key={n.id} 
                              className={`notification-item ${!n.is_read ? 'unread' : ''}`}
                              onClick={() => markNotificationAsRead(n.id)}
                            >
                              <div className="notification-item-content">
                                <h4>{n.title}</h4>
                                <p>{n.message}</p>
                                <span className="notification-time">
                                  {new Date(n.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                              {!n.is_read && <span className="unread-dot"></span>}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button className="profile-button" onClick={() => navigate('/talent/profile')}>
                  <div className="profile-avatar">
                    <img
                      src={talent?.profilePicture || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSI4IiB5PSI4Ij4KPHBhdGggZD0iTTIwIDIxVjE5QTQgNCAwIDAgMCAxNiAxNUg4QTQgNCAwIDAgMCA0IDE5VjIxIiBzdHJva2U9IiM5Q0E0QUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxjaXJjbGUgY3g9IjEyIiBjeT0iOSIgcj0iNCIgc3Ryb2tlPSIjOUNBNEFGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4KPC9zdmc+'}
                      alt="Profile"
                      className="profile-image"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNFNUU3RUIiLz4KPHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4PSI4IiB5PSI4Ij4KPHBhdGggZD0iTTIwIDIxVjE5QTQgNCAwIDAgMCAxNiAxNUg4QTQgNCAwIDAgMCA0IDE5VjIxIiBzdHJva2U9IiM5Q0E0QUYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxjaXJjbGUgY3g9IjEyIiBjeT0iOSIgcj0iNCIgc3Ryb2tlPSIjOUNBNEFGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4KPC9zdmc+';
                      }}
                    />
                  </div>
                  <div className="profile-info">
                    <span className="profile-name">{talent?.firstName} {talent?.lastName}</span>
                    {talent?.rating && talent.rating > 0 && (
                      <span className="profile-rating">
                        <Star size={12} fill="currentColor" /> {parseFloat(talent.rating as any || '0').toFixed(1)}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            </header>
          )}

          {/* Content Area */}
          <div className={`dashboard-content ${!isDashboardPage ? 'no-header' : ''}`}>
            {children}
          </div>
        </main>
      </div>
    
  );
};

export default TalentLayout; 